import { ChangeDetectionStrategy, Component, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SidebarComponent } from '@nutrix/sidebar';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { AccessLevelDirective, AuthService } from '@nutrix/data-access-auth';
import { UiDropdownComponent } from '@nutrix/ui-dropdown';
import { TooltipModule } from 'primeng/tooltip';
import { ToastModule } from 'primeng/toast';
import { UsersStateService } from '@nutrix/data-access-users';
import { ChatDataService, ChatStateService } from '@nutrix/data-access-chat';
import { MessageService } from 'primeng/api';
import { catchError, map, of, switchMap } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { OverlayBadgeModule } from 'primeng/overlaybadge';
import { CallService } from '@nutrix/data-access-calls';
import { CallWidgetComponent, IncomingCallComponent } from '@nutrix/feat-call';

@Component({
  selector: 'nutrix-feat-layout',
  imports: [
    CommonModule,
    SidebarComponent,
    UiDropdownComponent,
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    TooltipModule,
    ToastModule,
    ButtonModule,
    OverlayBadgeModule,
    AccessLevelDirective,
    CallWidgetComponent,
    IncomingCallComponent,
  ],
  templateUrl: './feat-layout.component.html',
  styleUrl: './feat-layout.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FeatLayoutComponent implements OnInit {
  readonly #authService = inject(AuthService);
  readonly #chatDataService = inject(ChatDataService);
  readonly #chatStateService = inject(ChatStateService);
  readonly #messageService = inject(MessageService);
  readonly #router = inject(Router);
  readonly #callService = inject(CallService);
  public usersStateService = inject(UsersStateService);
  public user = this.usersStateService.currentUser;
  currentUser = signal(this.#authService.loadCurrentUser());
  isAdmin = computed(() => this.currentUser()?.admin);
  canAccessAgenda = computed(
    () =>
      this.currentUser()?.featureAccess.teleconsultation.read ||
      this.currentUser()?.featureAccess.agendaScheduling.read,
  );
  isHr = computed(() => this.currentUser()?.featureAccess.hR.read);
  isLaboratory = computed(() => this.currentUser()?.featureAccess.laboratory.read);
  hasCaseManagerAccess = computed(
    () =>
      this.currentUser()?.featureAccess.caseManager.read ||
      this.currentUser()?.featureAccess.treatmentPlan.read ||
      this.currentUser()?.featureAccess.nutritionalPlan.read,
  );

  public getUnreadMessages$$ = this.#chatStateService.getUnreadMessages$$;

  hasUnreadMessages$ = this.getUnreadMessages$$.pipe(
    switchMap(() => {
      if (!(this.#authService.loadCurrentUser()?.featureAccess?.chat?.read ?? false)) {
        return of(false);
      }
      return this.#chatDataService.getUnreadMessagesAsync().pipe(
        map((messages) => messages.length > 0),
        catchError((error) => {
          this.#messageService.add({
            severity: 'error',
            summary: 'Error cargando mensajes no leidos',
            detail: error?.error?.detail,
          });
          return of(false);
        }),
      );
    }),
  );

  ngOnInit(): void {
    setInterval(() => {
      this.getUnreadMessages$$.next(true);
    }, 300000);

    // Start listening for WhatsApp calls (inbound + outbound answers) on any page.
    if (this.canAccessAgenda()) {
      void this.#callService.startPolling();
    }
  }

  async logout() {
    await this.#authService.logout();
    this.#router.navigate(['login']);
  }
}
